import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import type { ArtistRole } from '@/types/database';

interface ArtistTeamAccess {
  loading: boolean;
  role: ArtistRole | null;
  canModerate: boolean;
  canManage: boolean;
  canOwn: boolean;
}

const RANK: Record<ArtistRole, number> = {
  moderator: 1,
  admin: 2,
  owner: 3,
};

/**
 * Whether the current user is on this artist's team.
 *
 * - Platform admins (`is_admin`) are treated as `owner` on every artist.
 * - Otherwise looks up `artist_members` for (user_id, artist_id) and
 *   returns the role. Roles are ranked owner > admin > moderator.
 */
export function useArtistTeamAccess(
  artistId: string | undefined,
): ArtistTeamAccess {
  const { session, isAdmin } = useAuth();
  const userId = session?.user.id;
  const [role, setRole] = useState<ArtistRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !artistId) {
      setRole(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);

    void supabase
      .from('artist_members')
      .select('role')
      .eq('artist_id', artistId)
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        setRole((data?.role as ArtistRole | undefined) ?? null);
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [userId, artistId]);

  // Platform admins get owner-level rights by convention.
  const effective: ArtistRole | null = isAdmin ? 'owner' : role;
  const rank = effective ? RANK[effective] : 0;

  return {
    loading,
    role: effective,
    canModerate: rank >= RANK.moderator,
    canManage: rank >= RANK.admin,
    canOwn: rank >= RANK.owner,
  };
}
